import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { IDriverScore } from "@app/main/components/insights/driver-behaviour/driver-behaviour-comparison.component";
import { DynamicReportService } from "@app/main/components/reports/_services/dynamicReports.service";
import { IDrivingStyle, IDrivingStyleRagBound } from "@app/main/components/resourceManagement/driving-styles-manager/driving-styles.interfaces";
import { DrivingStylesService } from "@app/main/components/resourceManagement/driving-styles-manager/driving-styles.service";
import { IDashboardWidget } from "@app/main/modules/dashboard/_models/IDashboardWidget";
import { IFilterOptions } from "@interfaces/filters/IFilterOptions";
import { ReportDataFunction } from "@reports/enums/ReportDataFunction";
import { ChartService } from "@services/charts.service";
import { RadialGaugeOptions } from "canvas-gauges";
import { DashboardService } from "../../dashboard.service";
import "./gauge-widget.component.scss";

@Component({
    selector: 'gauge-widgetv3',
    template: require("./gauge-widget.component.html")
})
export class GaugeWidgetComponent implements OnInit, OnChanges {

    @Input() widget: IDashboardWidget;
    @Input() filterOptions: IFilterOptions;

    gaugeOptions: RadialGaugeOptions;
    drivingStyle: IDrivingStyleRagBound;
    driverScore: IDriverScore;
    chartConfig: Highcharts.Options;

    constructor(
        private readonly chartService: ChartService,
        private readonly dashboardService: DashboardService,
        private readonly drivingStylesService: DrivingStylesService,
        private readonly dynamicReportService: DynamicReportService
    ) { }

    ngOnInit(): void {
        if (this.widget) {
            this.setup();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["filterOptions"]) {
            this.updateDisplay();
        }

        this.setup();
    }

    // ---

    private setup(): void {
        this.widget.DataWidgetParameters.UseTotals = true;

        this.widget.onDataDefinitionUpdated = () => {
            setTimeout(() => {
                this.updateDisplay();
            }, 0);
        };

        this.dashboardService.dataSourceToWidgetDataDefinition(this.widget);
        this.dynamicReportService.filtersToDefinition(this.filterOptions, this.widget.dataDefinition);

        this.drivingStylesService.getDrivingStyles(false, true).then(styles => {

            const defaultDrivingStyle = this.getDefaultDrivingStyle(styles);

            if (!defaultDrivingStyle) {
                console.warn("No default driving style provided for gauge widget");
                return;
            }

            this.drivingStyle = defaultDrivingStyle;

            // Find one that matches our type
            var matchedStyle = defaultDrivingStyle.DrivingStyleMetricMappings.find(mapping => mapping.DrivingStyleMetric == this.widget.DataWidgetParameters.DrivingStyleMetric);

            if (matchedStyle) {
                this.drivingStyle = matchedStyle;
            }

            this.updateDisplay();
        });
    }

    private getDefaultDrivingStyle(drivingStyles: IDrivingStyle[]): IDrivingStyle {
        if (!drivingStyles?.length) {
            return;
        }

        const accountSpecificDefault = drivingStyles.find(drivingStyle => {
            return drivingStyle.DrivingStyleAssociations.find(association => !association.GroupId && !VehicleType[association.VehicleType] && association.VehicleType !== VehicleType.None)
        });

        if (accountSpecificDefault) {
            return accountSpecificDefault;
        }

        // return first style, which will naturally be first in DB (app default)
        return drivingStyles[0];
    }

    private updateDisplay() {
        if (!this.drivingStyle || !this.widget.dataDefinition) {
            return;
        }

        var results = this.dashboardService.convertDataDefinitionToMetricSeries(this.widget.dataDefinition, this.widget.DataWidgetParameters.UseTotals);

        if (this.widget.noData || results.length == 0) {
            return;
        }

        var value = this.dashboardService.aggregateChartSeries(results, ReportDataFunction.Average);

        var minValue = 0;
        var maxValue = 100;

        var lowerRagBound = this.drivingStyle.LowerRagBound;
        var upperRagBound = this.drivingStyle.UpperRagBound;

        var highlights: CanvasGauges.Highlight[] = [];

        var redColour = "rgba(200, 50, 50, .75)";
        var amberColour = "rgb(236, 170, 56)";
        var greenColour = "rgb(92, 167, 147)";

        highlights.push({ "from": minValue, "to": lowerRagBound, "color": redColour });
        highlights.push({ "from": lowerRagBound, "to": upperRagBound, "color": amberColour });
        highlights.push({ "from": upperRagBound, "to": maxValue, "color": greenColour });

        this.gaugeOptions = this.chartService.getDefaultGaugeChartOptions(value, minValue, maxValue, highlights);

        this.driverScore = {
            lowerRagBound,
            upperRagBound,
            score: value,
            title: ""
        };
    }
}
